import { propertyFetch } from '../services/idxPropertyClient';
import {searchSnapshot,searchQuery,assertValidSearch,readSearchError} from './propertySearchFilters';
import {mergeChatProperties} from './chatPropertySearch';

export const DELIVERY_VERSION='bounded-search-v1';
const endpoint='https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary';
const fingerprint=params=>JSON.stringify(Object.fromEntries(Object.entries(searchSnapshot(params)).sort(([a],[b])=>a.localeCompare(b))));
async function responseData(response) {
  if (!response.ok) {
    const error=new Error(await readSearchError(response));
    error.restartSearch=response.status===409;
    throw error;
  }
  return response.json();
}

// Two ordered streams: fetch MLS pages only as needed, and fetch private inventory once.
// Private homes never repeat on later pages or jump ahead of unseen MLS price results.
export async function loadPropertyPage({params,siteId,privateApiKey,fetchImpl=fetch,pageSize=24,pageState=null,queryOptions={},signal,includePrivate=true}) {
  params=searchSnapshot(params);assertValidSearch(params);
  if (!Number.isInteger(pageSize)||pageSize<1||pageSize>100) throw new Error('Invalid page size.');
  const key=fingerprint(params);
  if (pageState && pageState.key!==key) throw new Error('The search changed. Start again to load more homes.');
  const state=pageState?{...pageState,mls:[...pageState.mls],privateRows:[...pageState.privateRows]}:{key,mls:[],privateRows:[],cursor:null,serverMore:true,started:false,privateUnavailable:false,searchMeta:null};
  const seen=new Set(state.seen||[]);
  const identity=row=>row.listing_key??`${row.isPrivate?'private':'mls'}-${row.id}`;
  const fetchMls=async()=>{
    const query=searchQuery(params);query.set('pageSize',String(pageSize));
    for(const k of ['firstPhoto','hasMedia']) if(queryOptions[k]!==undefined) query.set(k,String(queryOptions[k]));
    if(state.cursor) query.set('cursor',state.cursor);
    const data=await responseData(await propertyFetch(`${endpoint}/page?${query}`, {signal}, fetchImpl));
    if(!Array.isArray(data.properties)||data.pagination?.version!==DELIVERY_VERSION||typeof data.pagination.has_more!=='boolean'||data.properties.length>pageSize||(data.pagination.has_more&&!data.pagination.next_cursor)) throw new Error('Property search is temporarily unavailable. Please try again.');
    if(data.pagination.has_more && (!data.properties.length || data.pagination.next_cursor===state.cursor)) throw new Error('Unable to load the next page. Start the search again.');
    state.mls.push(...data.properties.filter(row=>!seen.has(identity(row))));state.serverMore=data.pagination.has_more;state.cursor=data.pagination.next_cursor;state.searchMeta=data.search_meta;state.started=true;
  };
  if(!state.started) {
    const privateQuery=new URLSearchParams({site_id:siteId,status:'available'});
    const privateRequest=(async()=>{
      if(!includePrivate)return;
      try {
        const response=await fetchImpl(`https://tophamtechbe.com/api/treas/private/v1/listings?${privateQuery}`,{headers:{'x-api-key':privateApiKey},signal});
        if(!response.ok) throw new Error();const data=await response.json();if(!Array.isArray(data.listings)) throw new Error();
        const unique=[...new Map(data.listings.map(row=>[row.id,row])).values()];
        state.privateRows=mergeChatProperties([],unique,params);
      } catch(error) { if(error.name==='AbortError') throw error;state.privateUnavailable=true; }
    })();
    await Promise.all([fetchMls(),privateRequest]);
  }
  const properties=[];
  while(properties.length<pageSize) {
    while(!state.mls.length&&state.serverMore) await fetchMls();
    if(!state.mls.length&&!state.privateRows.length) break;
    let takePrivate=!state.mls.length;
    if(state.mls.length&&state.privateRows.length&&['lowhigh','highlow'].includes(params.priceSort)) {
      const m=state.mls[0].current_price??state.mls[0].list_price;
      const p=state.privateRows[0].current_price;
      // MySQL orders NULL first for ascending price and last for descending price.
      takePrivate=m==null?params.priceSort==='highlow':params.priceSort==='lowhigh'?Number(p)<Number(m):Number(p)>Number(m);
    }
    const row=takePrivate?state.privateRows.shift():state.mls.shift();
    if(seen.has(identity(row)))continue;
    seen.add(identity(row));properties.push(row);
  }
  state.seen=[...seen];
  return {properties,pageState:state,hasMore:state.serverMore||state.mls.length>0||state.privateRows.length>0,privateUnavailable:state.privateUnavailable,searchMeta:state.searchMeta};
}

export async function loadPropertyMap({params,zoom=4,bounds,fetchImpl=fetch,signal}) {
  params=searchSnapshot(params);assertValidSearch(params);
  const query=searchQuery(params);query.set('zoom',String(Math.max(0,Math.min(22,Math.floor(zoom)))));
  if(bounds) for(const key of ['north','south','east','west']) query.set(key,String(bounds[key]));
  const data=await responseData(await propertyFetch(`${endpoint}/map?${query}`, {signal}, fetchImpl));
  if(data.map?.version!==DELIVERY_VERSION||!Array.isArray(data.map.features)||data.map.features.length>=900||!Number.isFinite(data.map.match_count)) throw new Error('Map search is temporarily unavailable. Please try again.');
  return {map:data.map,searchMeta:data.search_meta};
}

export async function loadPrivateMapRows({params,siteId,privateApiKey,fetchImpl=fetch,signal}) {
  try {
    const query=new URLSearchParams({site_id:siteId,status:'available'});
    const response=await fetchImpl(`https://tophamtechbe.com/api/treas/private/v1/listings?${query}`,{headers:{'x-api-key':privateApiKey},signal});
    if(!response.ok) throw new Error();const data=await response.json();if(!Array.isArray(data.listings)) throw new Error();
    return {properties:mergeChatProperties([], [...new Map(data.listings.map(row=>[row.id,row])).values()],params),privateUnavailable:false};
  } catch(error) {if(error.name==='AbortError')throw error;return {properties:[],privateUnavailable:true};}
}
